import React, { useState, useEffect } from "react";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Globe,
  DollarSign,
} from "lucide-react";
import { useBusiness } from "../contexts/BusinessContext";
import { supabase } from "../lib/supabase";
import BusinessLogoUpload from "./BusinessLogoUpload";

export default function BusinessSettingsForm() {
  const { selectedBusiness, updateBusiness } = useBusiness();
  if (!selectedBusiness) return null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalFormData, setOriginalFormData] = useState({
    name: selectedBusiness?.name || "",
    type: selectedBusiness?.type || "",
    email: selectedBusiness?.email || "",
    email_verified: selectedBusiness?.email_verified || false,
    phone: selectedBusiness?.phone || "",
    address: selectedBusiness?.address || "",
    timezone: selectedBusiness?.timezone || "America/Vancouver",
    currency: selectedBusiness?.currency || "USD",
    gst_number: selectedBusiness?.gst_number || "",
    pst_number: selectedBusiness?.pst_number || "",
    business_hours: selectedBusiness?.business_hours || {
      monday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      tuesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      wednesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      thursday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      friday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      saturday: { enabled: true, open: "10:00", close: "15:00", closed: false },
      sunday: { enabled: false, open: "09:00", close: "17:00", closed: true }
    },
  });
  const [formData, setFormData] = useState({...originalFormData});

  
  // Track if form has changes
  const hasChanges = React.useMemo(() => {
    return (
      formData.name !== originalFormData.name ||
      formData.email !== originalFormData.email ||
      formData.phone !== originalFormData.phone ||
      formData.address !== originalFormData.address ||
      formData.timezone !== originalFormData.timezone ||
      formData.currency !== originalFormData.currency ||
      formData.gst_number !== originalFormData.gst_number ||
      formData.pst_number !== originalFormData.pst_number ||
      JSON.stringify(formData.business_hours) !== JSON.stringify(originalFormData.business_hours)
    );
  }, [formData, originalFormData]);

  // Initialize form data on component mount or business change
  useEffect(() => {
    if (selectedBusiness) {
      const newData = {
        name: selectedBusiness.name || "",
        type: selectedBusiness.type || "",
        email: selectedBusiness.email || "",
        email_verified: selectedBusiness.email_verified || false,
        phone: selectedBusiness.phone || "",
        address: selectedBusiness.address || "",
        timezone: selectedBusiness.timezone || "America/Vancouver",
        currency: selectedBusiness.currency || "USD",
        gst_number: selectedBusiness.gst_number || "",
        pst_number: selectedBusiness.pst_number || "",
        business_hours: selectedBusiness.business_hours || {
          monday: { enabled: true, open: "09:00", close: "17:00", closed: false },
          tuesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
          wednesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
          thursday: { enabled: true, open: "09:00", close: "17:00", closed: false },
          friday: { enabled: true, open: "09:00", close: "17:00", closed: false },
          saturday: { enabled: true, open: "10:00", close: "15:00", closed: false },
          sunday: { enabled: false, open: "09:00", close: "17:00", closed: true }
        },
      };
      setOriginalFormData(newData);
      setFormData(newData);
    }
  }, [selectedBusiness]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Skip submission if no actual changes
    if (!hasChanges) {
      setLoading(false);
      return;
    }

    try {
      if (!supabase) {
        throw new Error("Supabase client not initialized");
      }

      const { error } = await supabase
        .from("businesses")
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          timezone: formData.timezone,
          currency: formData.currency,
          gst_number: formData.gst_number,
          pst_number: formData.pst_number,
          business_hours: formData.business_hours,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedBusiness.id);

      if (error) throw error;

      setSuccess("Business settings updated successfully");
      await updateBusiness(selectedBusiness.id, formData);
      // Update original form data to match current data after successful save
      setOriginalFormData({...formData});
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update business settings"
      );
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({...originalFormData});
    setError(null);
    setSuccess(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-3 sm:py-4 rounded-xl text-sm sm:text-base">
          {error}
        </div>
      )}

      <div className="w-full">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
            Business name
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
          Business address
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, address: e.target.value }))
          }
          className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
          Contact Email
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, email: e.target.value }))
          }
          className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
            Phone number
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, phone: e.target.value }))
            }
            className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
            Time zone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, timezone: e.target.value }))
            }
            className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
          >
            <option value="America/Vancouver">(GMT-08:00) Pacific Time</option>
            <option value="America/Denver">(GMT-07:00) Mountain Time</option>
            <option value="America/Chicago">(GMT-06:00) Central Time</option>
            <option value="America/New_York">(GMT-05:00) Eastern Time</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
            Currency
          </label>
          <select
            value={formData.currency}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, currency: e.target.value }))
            }
            className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
          >
            <option value="USD">USD ($)</option>
            <option value="CAD">CAD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
            GST Number (optional)
          </label>
          <input
            type="text"
            value={formData.gst_number}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, gst_number: e.target.value }))
            }
            className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
            placeholder="Enter GST number"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
          PST Number (optional)
        </label>
        <input
          type="text"
          value={formData.pst_number}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, pst_number: e.target.value }))
          }
          className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
          placeholder="Enter PST number"
        />
      </div>

      {/* Business Logo Upload Section */}
      <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
        <BusinessLogoUpload size="medium" />
      </div>

      {/* Mobile-optimized Business Hours Section */}
      <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
        <label className="block text-sm font-semibold text-gray-900 mb-3 sm:mb-4">
          Business Hours
        </label>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
          Set your weekly operating hours. These hours are used for booking availability and scheduling.
        </p>
        
        <div className="space-y-3 sm:space-y-4">
          {Object.entries(formData.business_hours).map(([day, hours]) => {
            const dayKey = day as keyof typeof formData.business_hours;
            return (
              <div key={day} className="bg-white rounded-xl border border-gray-200/50 p-3 sm:p-4">
                {/* Mobile-first layout for business hours */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          business_hours: {
                            ...prev.business_hours,
                            [dayKey]: {
                              ...prev.business_hours[dayKey],
                              enabled: !prev.business_hours[dayKey].enabled,
                              closed: !prev.business_hours[dayKey].enabled
                            }
                          }
                        }));
                      }}
                      className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 touch-manipulation ${
                        hours.enabled ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                          hours.enabled ? 'translate-x-6 sm:translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm sm:text-base font-medium text-gray-900 capitalize min-w-[80px] sm:min-w-[100px]">
                      {day}
                    </span>
                  </div>
                  
                  {hours.enabled ? (
                    <div className="flex items-center justify-center sm:justify-end">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              business_hours: {
                                ...prev.business_hours,
                                [dayKey]: {
                                  ...prev.business_hours[dayKey],
                                  open: e.target.value
                                }
                              }
                            }));
                          }}
                          className="w-20 sm:w-24 h-9 sm:h-10 rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-xs sm:text-sm transition-all duration-200"
                        />
                        <span className="text-gray-500 text-sm">to</span>
                        <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              business_hours: {
                                ...prev.business_hours,
                                [dayKey]: {
                                  ...prev.business_hours[dayKey],
                                  close: e.target.value
                                }
                              }
                            }));
                          }}
                          className="w-20 sm:w-24 h-9 sm:h-10 rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-xs sm:text-sm transition-all duration-200"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center sm:justify-end">
                      <span className="text-gray-500 font-medium text-sm sm:text-base">Closed</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 p-3 sm:p-4 bg-blue-50 rounded-xl">
          <p className="text-xs sm:text-sm text-blue-700">
            <strong>Note:</strong> These hours determine when customers can book appointments online and when your business is considered available for scheduling.
          </p>
        </div>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 sm:px-6 py-3 sm:py-4 rounded-xl text-sm sm:text-base">
          {success}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-center md:justify-end mt-6 gap-3 sm:gap-4">
        {hasChanges && (
          <button
            type="button"
            onClick={resetForm}
            className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 active:scale-95 order-2 sm:order-1"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !hasChanges}
          className={`w-full sm:w-auto px-6 py-3 text-sm sm:text-base font-semibold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 active:scale-95 order-1 sm:order-2 ${
            hasChanges && !loading
              ? 'bg-purple-700 hover:bg-purple-800 focus:ring-purple-500'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
